import '@avodah-inc/connect-ui/dist/styles.css';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { useEffect, type ReactElement, type ReactNode } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import client from '@/api/graphqlClient';
import Layout from './layout';
import { AuthProvider } from '@/hooks/AuthProvider';
import StoreProvider from '@/store/StoreProvider';
import '@/styles/index.css';
import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import i18n from '@i18n/i18n.config';
import useVersionSkewProtection from '@/hooks/useVersionSkewProtection';
import { useAppConfig } from '@/hooks/useAppConfig';

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  useVersionSkewProtection();

  const getLayout = Component.getLayout ?? (page => page);
  useAppConfig();

  useEffect(() => {
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
    document.documentElement.lang = savedLang || i18n.language;
    document.documentElement.dir = (savedLang === 'ar') ? 'rtl' : 'ltr';
  // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [i18n.language]);

  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <StoreProvider>
          <Layout>
            <ToastContainer
              position="bottom-right"
              autoClose={6000}
              hideProgressBar={false}
              newestOnTop
              pauseOnFocusLoss
              draggable
              pauseOnHover
              limit={3}
              closeButton={false}
              transition={Slide}
            />
            {getLayout(<Component {...pageProps} />)}
          </Layout>
        </StoreProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default MyApp;
